import { random } from 'lodash';

import { Store } from '@ngrx/store';
import { DesignState } from '@design/store/design.reducer';
import { Edge } from '@design/models/edge.model';
import { Vertex } from '@design/models/vertex.model';
import { ConfigurationIds } from '@design/models/ui-state.model';
import { RoofSection, flatRoofDefaults } from '@design/models/roof-section.model';
import { Obstacle, ObstacleType } from '@design/models/obstacle.model';
import { PRESET_COLORS_DEFAULT, DEFAULT_ROOF_OUTLINE_COLOR } from '@lib-ui/lib/common/types/enactColorpick';
import { fromRoofSectionActions, fromRoofSectionSelectors } from '@design/store/roof-section';
import { fromEdgeActions, fromEdgeSelectors } from '@design/store/edge';
import { fromVertexActions, fromVertexSelectors } from '@design/store/vertex';
import { fromObstacleActions, fromObstacleSelectors } from '@design/store/obstacle';
import { fromUiActions, fromUiSelectors } from '@design/store/ui';
import { fromTreesActions } from '@design/store/trees';
import { take } from 'rxjs/operators';
import { timer } from 'rxjs';
import { Vector3, MathUtils, BufferGeometry, Mesh, Line, Object3D, InstancedMesh } from 'three';
import { SceneStoreSyncService } from '@design/services/scene-store-sync.service';

import Util, { MeshUserData, meshTypeEnum, ObstacleData, panelDims, PanelStoreData, notificationMessages, EdgeInterface, constants, storeIdPrefix } from './Utility';
import { RoofTopology } from './RoofTopology';
import { RoofGeometry } from './roofGeometry';
import { fromPanelActions } from '@design/store/panel';
import { Panel, PanelOrientation } from '@design/models/panel.model';
import { fromPanelArrayActions } from '@design/store/panel-array';
import { PanelArray } from '@design/models/panel-array.model';
import { generateUniqueId } from '@design/utils/generateUnique';
import { openNotificationService } from '@lib-ui/lib/atomic/enact-alert-snack-bar/openNotification.service';
import { fromPanelArraySelectors } from '@design/store/panel-array/panel-array.selector';
import { EngineService } from '../threejs.service';
import { fromPanelSelectors } from '@design/store/panel/panel.selector';
import { fromFrameActions } from '@design/store/frame';
import { Frame } from '@design/models/frame.model';
import { TranslateService } from '@ngx-translate/core';
import { fromUndoRedoActions } from '@design/store/undo-redo/undo-redo.action';
import { fromMetaDataSelectors } from '@design/store/meta-data';

export class StoreUtil {
    store: Store<DesignState>;

    constructor(store: Store<DesignState>, private notificationService: openNotificationService,
        private engServ: EngineService,
        private translate : TranslateService,
        private sceneStoreSyncService: SceneStoreSyncService | null
        ) {
        this.store = store;
   
    }

    /**
     * Gets the current roof outline color from the store.
     * Priority order:
     * 1. Use the color from the NgRx store (current design session)
     * 2. If store has default, check localStorage for project-level preference
     * 3. Default to White (#FFFFFF) for new projects
     */
    private getCurrentRoofOutlineColor(): string {
        let currentColor = DEFAULT_ROOF_OUTLINE_COLOR;
        this.store.select(fromUiSelectors.getRoofOutlineColor)
            .pipe(take(1))
            .subscribe(color => {
                if (color) {
                    currentColor = color;
                }
            });
        
        // If store has default color, check localStorage for project-level preference
        if (currentColor === DEFAULT_ROOF_OUTLINE_COLOR) {
            const projectId = localStorage.getItem('crn');
            if (projectId) {
                const projectColorKey = `roofOutlineColor_${projectId}`;
                const savedProjectColor = localStorage.getItem(projectColorKey);
                if (savedProjectColor) {
                    currentColor = savedProjectColor;
                }
            }
        }
        
        return currentColor;
    }

    storeRoofSection(roofId: string, roofGeometry: RoofGeometry, allStoreVerts: Vertex[], allSotreEdges: Edge[], overlappingRoofs: string[], gutterEdge: EdgeInterface, ridgeEdge: EdgeInterface, pitchDeg: number) {
        // vertexList.push(vertexList[0]); // copy the first vertex at the end to close loop
        // this code is referenced from the roofSectionCreated() function in draw-area.component.ts
        // roofId = roofId;
        // this.rt.name = roofId;

        let allStoreRoofs: RoofSection[] = [];
        this.store
            .select(fromRoofSectionSelectors.allRoofSections)
            .pipe(take(1))
            .subscribe((v) => allStoreRoofs = v)

        const vertexList = roofGeometry.fittedVertexList;
        const lineList = roofGeometry.fittedLineList;
        const confirmedPlane = roofGeometry.fitPlane?.confirmedPlane;
        const roofMesh = roofGeometry.fittedRoof;
        const roofCenter = Util.getCenterPoint(roofMesh!);

        const roofArea = Util.calculateArea(roofMesh);
        const height = Math.min(...vertexList.map(m => m.position).map(pt => pt.z))

        const azimuthDeg = Util.calculateAzimuth(confirmedPlane, gutterEdge.startVertex.position, gutterEdge.endVertex.position, roofCenter, pitchDeg);
        let defaultSetback = pitchDeg == flatRoofDefaults.pitch ? flatRoofDefaults.setbackFt : 1.5;
        let preferencePresent = false;
        // Fetching user preferences for roof setback
        // This is done for new roofs & roof pitch change
        this.store.select(fromMetaDataSelectors.selectPreference)
            .pipe(take(1))
            .subscribe((obj: any) => {
                const preferencesObj = obj?.preferencesObj;
                if (!preferencesObj || preferencesObj === null) return;
                if (
                    preferencesObj?.system_design?.setback?.value !== undefined &&
                    preferencesObj?.system_design?.setback?.value !== null &&
                    preferencesObj?.system_design?.setback?.visible
                ) {
                    defaultSetback = preferencesObj.system_design.setback.value;
                    preferencePresent = true;
                }
            });


        const vertices: Vertex[] = []
        const storeVertsMap = new Map(allStoreVerts.map(v => [v.id, v]));
        vertexList.forEach(vert => {
            const userData = vert.userData as MeshUserData

            if (!userData.enactStoreId) {
                return
            }

            if (userData.addedToStore) {
                const prevVert = storeVertsMap.get(userData.enactStoreId);
                if (prevVert) {
                    prevVert.association.push(roofId);                
                    vertices.push({ ...prevVert });
                }

            }
            else {

                const pos = vert.position;
                const id = userData.enactStoreId;

                const vertex: Vertex = {
                    id: id,
                    x: pos.x,
                    y: pos.y,
                    z: pos.z,
                    association: [roofId],
                }

                const topoVertexInd = RoofTopology.getVertexId(vert);
                if (topoVertexInd) {
                    RoofTopology.setVertexPos(topoVertexInd, vert, true)
                }
                userData.addedToStore = true;
                vertices.push(vertex);
            }
        })

        const edges: Edge[] = []
        const edgeMap = new Map(allSotreEdges.map(edge => [edge.id, edge]));
        lineList.forEach((line, i) => {
            const userData = line.edge.userData as MeshUserData

            if (!userData.enactStoreId) {
                return
            }

            const id = userData.enactStoreId;

            if (userData.addedToStore && userData.enactStoreId) {
                // if it already added to store then add the current roof to Edge association array

                const prevEdge = edgeMap.get(id);
                if (prevEdge) {
                    prevEdge.association.push(roofId);                
                    edges.push({ ...prevEdge });
                }

            }
            else {
                let translatedName = '';
                this.translate.get('Edge').pipe(take(1)).subscribe(res => {
                    translatedName = res;
                })
                const edge: Edge = {
                    id: id,
                    name: `${translatedName} ${i + 1}`,
                    color: this.getCurrentRoofOutlineColor(),
                    configurationId: ConfigurationIds.Edge,
                    startVertexId: line.startVertex.name,
                    endVertexId: line.endVertex.name,
                    setback: defaultSetback,
                    setbackVisible: false,
                    association: [roofId],
                };

                userData.addedToStore = true;
                edges.push(edge)
            }

        });

        const storeGutter = edges.filter(e => e.id == gutterEdge?.edge.name)[0];
        // If user preference for setback is not present, apply default gutter setback logic
        if (!preferencePresent) {
            storeGutter.setback = (pitchDeg === flatRoofDefaults.pitch)
            ? flatRoofDefaults.setbackFt
            : 0; // For tilted roofs, gutter setback is 0
        }
        const storeRidge = edges.filter(e => e.id == ridgeEdge?.edge.name)[0];

        let translatedName = '';
        this.translate.get('Roof section').pipe(take(1)).subscribe(res => {
            translatedName = res;
        })
        if(allStoreRoofs.length > 0){
            const nameSplit = allStoreRoofs[allStoreRoofs.length -1].name .split(" ");
            var lastRoofNameNumber = parseInt(nameSplit[nameSplit.length -1].trim());
        }else{
            lastRoofNameNumber = allStoreRoofs.length;
        }
        const newRoofSection = new RoofSection({
            area: Util.getAreaPixelToFeet(roofArea),
            setback: defaultSetback,  // Util.getMeasurementPixelToFeet(5), // default 0 - 15 is for testing panel setback
            setbackVisible: true,
            azimuth: azimuthDeg, // angle between z and roof normal
            pitch: pitchDeg, // angle with ground
            parapit: pitchDeg == 0 ? random(1, 100) : 0, // parapit is disabled if pitch is enabled
            height: Util.getMeasurementPixelToFeet(height), // bottom roof part elevation
            color: this.getCurrentRoofOutlineColor(),
            overlappingRoofs: overlappingRoofs,
            edgeIds: edges.map((e) => e.id),
            vertexIds: vertices.map((v) => v.id),
            id: roofId,
            defaultHeight: Util.getMeasurementPixelToFeet(height),
            name: `${translatedName} ` + (lastRoofNameNumber + 1).toString(),
            gutterId: storeGutter.id,
            ridgeId: storeRidge.id,
            alignPanels: storeGutter.id,
        });


        this.store.dispatch(fromEdgeActions.AddMany({ edges: edges }));

        this.store.dispatch(fromVertexActions.AddMany({ vertices: vertices }));

        //update overlapping roofs
        // overlappingRoofs.forEach(oRoofId => {
        //     this.store.select(fromRoofSectionSelectors.selectRoofById([oRoofId])).pipe(take(1)).subscribe(rs => {
        //         if (rs[0]!) {
        //             let rscopy = JSON.parse(JSON.stringify(rs[0]));
        //             let temproofs: string[] = []
        //             if (rscopy.overlappingRoofs) {
        //                 temproofs = [...rscopy.overlappingRoofs] as string[];
        //             }
        //             temproofs.push(roofId);
        //             rscopy.overlappingRoofs = temproofs
        //             this.store.dispatch(fromRoofSectionActions.RoofSectionConfigurationChange({ roof: rscopy }));

        //         }
        //     })
        // })

        // Clear selection if not protected
        this.store.select(fromUiSelectors.isSelectionProtected)
            .pipe(take(1))
            .subscribe(isProtected => {
                if (!isProtected) {
                    this.store.dispatch(fromUiActions.ClearSelection());
                }
                this.engServ.updateRoofSelectedState(newRoofSection.id);
            });
        // select the created roof..

        return newRoofSection;
    }

    storeDrawPanelShape(vertexList: Mesh[], lineList: Line[], roofId: string) {
        // TODO: store the draw shape as RoofSection and use that roofId
        const boundaryVertices: Vertex[] = [];
        vertexList.forEach(vert => {
            const userData = vert.userData as MeshUserData

            if (!userData.enactStoreId) {
                return
            }
            const pos = vert.position;
            const id = userData.enactStoreId;

            const vertex: Vertex = {
                id: id,
                x: pos.x,
                y: pos.y,
                z: pos.z,
                association: [roofId],
            }

            const topoVertexInd = RoofTopology.getVertexId(vert);
            if (topoVertexInd) {
                RoofTopology.setVertexPos(topoVertexInd, vert, true)
            }
            (vert.userData as MeshUserData).addedToStore = true;
            boundaryVertices.push(vertex);
        })

        const boundaryEdges: Edge[] = []
        lineList.forEach((line, i) => {
            const userData = line.userData as MeshUserData

            if (!userData.enactStoreId) {
                return
            }
            let translatedName = '';
            this.translate.get('Edge').pipe(take(1)).subscribe(res => {
                translatedName = res;
            })
            const id = userData.enactStoreId;
            const edge: Edge = {
                id: id,
                configurationId: ConfigurationIds.Edge,
                startVertexId: boundaryVertices[i].id,
                endVertexId: boundaryVertices[i + 1]?.id ?? boundaryVertices[0].id,
                setback: 0,
                setbackVisible: false,
                color: this.getCurrentRoofOutlineColor(),
                name: `${translatedName} ${i}`,
                association: [roofId],
            };

            userData.addedToStore = true;
            boundaryEdges.push(edge)

        });

        this.store.dispatch(fromVertexActions.AddMany({ vertices: boundaryVertices }));
        this.store.dispatch(fromEdgeActions.AddMany({ edges: boundaryEdges }));


        return [boundaryVertices, boundaryEdges]
    }

    updateMultiplePanelModule(panelArrayObject: Array<any>, _triggerURSnapshot?: boolean){
        const panelsToBeAdded: any = [];
        const framesToBeAdded: any = [];
        const panelArraysToBeUpdated: any = [];
        panelArrayObject.forEach(pArr=>{
                const isEastWest = pArr.panelArray.arrayType == '2';//TODO: change to east west

                const {noOfPanelEast, noOfPanelWest} = pArr.eastWestCount;
                const panels: Panel[] = pArr.panelsUserdataArray;
                const interrowSpacing = pArr.panelArray.interrowspacing ?? (pArr.panelArray.orientation == PanelOrientation.landscape ? pArr.panelArray.panelWidth : pArr.panelArray.panelLength);
                const defaultInterrowspacing = pArr.panelArray.defaultInterrowspacing ?? (pArr.panelArray.orientation == PanelOrientation.landscape ? pArr.panelArray.panelWidth : pArr.panelArray.panelLength);
                let translatedName = '';
                this.translate.get('Panel Array').pipe(take(1)).subscribe(res => {
                    translatedName = res;
                })
                const noOfPanels = panels.filter(p=>p.visible).length;
                const totalWatts = noOfPanels * pArr.panelArray.wattsPerModule;
                const panelArray: PanelArray = new PanelArray({
                    ...pArr.panelArray,
                    roofTilt: pArr.panelStoreData.roofPitch,
                    interrowspacing: Math.round(interrowSpacing * 100) / 100,
                    defaultInterrowspacing: Math.round(defaultInterrowspacing * 100) / 100,
                    panelIds: panels.map((p) => p.id),
                    edges: pArr.shapeEdgeIds,
                    vertices: pArr.shapeVertexIds,
                    //name: pArr.existingPanelArray ? pArr.existingPanelArray.name : `${translatedName} - ` + allPanels.length + 1,
                    totalAddedPanels: panels.length,
                    numberOfPanels: noOfPanels,
                    frameIds: pArr.frames.map((frame: any)=> frame.id),
                    noOfPanelEast: isEastWest ? noOfPanelEast : noOfPanels,
                    noOfPanelWest: noOfPanelWest,
                    totalWatts: totalWatts,
                });
                framesToBeAdded.push(...pArr.frames);
                panelsToBeAdded.push(...panels);
                panelArraysToBeUpdated.push(panelArray);
        })
        
        // Execute batch operations
        if(panelsToBeAdded && panelsToBeAdded.length > 0){
            this.store.dispatch(fromPanelActions.AddMany({ panels: panelsToBeAdded }));
        }
        if(framesToBeAdded && framesToBeAdded.length > 0){
            this.store.dispatch(fromFrameActions.AddMany({ frames: framesToBeAdded }));
        }
        if(panelArraysToBeUpdated && panelArraysToBeUpdated.length > 0){
            this.store.dispatch(fromPanelArrayActions.UpsertManyModule({ panelArray: panelArraysToBeUpdated }));
        }
    }

    storePanels(shapeVertexIds: string[], shapeEdgeIds: string[], panelsUserdataArray: Array<Panel>, panelStoreData: PanelStoreData, frames: Array<any>, eastWestCount: any, existingPanelArray?: PanelArray, triggerURSnapshot?: boolean) {

        // const panelMeshes = panelArrayGroup.children as Mesh[];
        const metadata = panelStoreData.storeMetadata;

        metadata.panelLength = Number(metadata.panelLength);
        metadata.panelWidth = Number(metadata.panelWidth);
        metadata.interrowspacing = Number(metadata.interrowspacing ?? 0);
        metadata.defaultInterrowspacing = Number(metadata.defaultInterrowspacing ?? 0);
        let hasPanelAdded = false
        this.store.select(fromPanelArraySelectors.allPanelArrays)
            .pipe(take(1))
            .subscribe(allPanels => {
                const isEastWest = metadata.arrayType == '2';//TODO: change to east west
                let degradation = 0.5;
                this.store.select(fromMetaDataSelectors.selectPreference)
                    .pipe(take(1))
                    .subscribe((obj: any) => {
                        const preferencesObj = obj?.preferencesObj;
                        if (!preferencesObj) return;
                        // Set panel degradation from preferences
                        if (
                            preferencesObj?.system_design?.panel_degradation !== undefined &&
                            preferencesObj?.system_design?.panel_degradation?.visible &&
                            preferencesObj?.system_design?.panel_degradation != null
                        ) {
                            degradation = preferencesObj.system_design.panel_degradation.value;
                        }
                    });
                const {noOfPanelEast, noOfPanelWest} = eastWestCount;
                const panels: Panel[] = panelsUserdataArray;
                const numberofPanel = panels.length;
                const interrowSpacing = metadata.interrowspacing ?? (metadata.orientation == PanelOrientation.landscape ? metadata.panelWidth : metadata.panelLength);
                const defaultInterrowspacing = metadata.defaultInterrowspacing ?? (metadata.orientation == PanelOrientation.landscape ? metadata.panelWidth : metadata.panelLength);
                let translatedName = ''
                this.translate.get('Panel Array').pipe(take(1)).subscribe(res => {
                    translatedName = res;
                });
                if(allPanels.length > 0){
                    const nameSplit = allPanels[allPanels.length -1].name .split(" ");
                    var lastPanelNameNumbering = parseInt(nameSplit[nameSplit.length -1].trim());
                    degradation = allPanels[0].degradation;
                }else{
                    lastPanelNameNumbering = allPanels.length;
                }
                const noOfPanels = panels.filter(p=>p.visible).length;
                metadata.totalWatts = noOfPanels * metadata.wattsPerModule;
                const panelArray: PanelArray = new PanelArray({
                    ...metadata,
                    roofTilt: panelStoreData.roofPitch,
                    interrowspacing: Math.round(interrowSpacing * 100) / 100,
                    defaultInterrowspacing: Math.round(defaultInterrowspacing * 100) / 100,
                    panelIds: panels.map((p) => p.id),
                    edges: shapeEdgeIds,
                    vertices: shapeVertexIds,
                    name: existingPanelArray ? existingPanelArray.name : `${translatedName} - ` + (lastPanelNameNumbering + 1).toString(),
                    totalAddedPanels: panels.length,
                    numberOfPanels: noOfPanels,
                    frameIds: frames.map(frame=> frame.id),
                    noOfPanelEast: isEastWest ? noOfPanelEast : noOfPanels,
                    noOfPanelWest: noOfPanelWest,
                    degradation:degradation
                });
                if(!noOfPanels){
                    this.notificationService.sendMessage({
                        message: this.translateMsg('toastrMsgs.insufRoofSpace'),
                        type: 'danger',
                        positionClass: "toast-bottom-left",
                        timeOut: 5000
                      });
                    return
                }

                //frames
                this.store.dispatch(fromPanelActions.AddMany({ panels }));
                this.store.dispatch(fromFrameActions.AddMany({frames: frames}))
                if (existingPanelArray) {
                    this.store.dispatch(fromPanelArrayActions.UpsertOne({ panelArray}));
                    if(triggerURSnapshot == undefined || triggerURSnapshot)
                      this.store.dispatch(fromUndoRedoActions.TriggerURSnapshot({action: fromPanelArrayActions.AddPanelArray, notification: panelArray.name+ ' '+ this.translateMsg('design.undoRedo.update')}))
                } else {
                                        // Protect UI during panel operations
                    this.store.dispatch(fromUiActions.ProtectSelection({ 
                        operationType: 'panel-placement' 
                    }));
                    
                    this.store.dispatch(fromPanelArrayActions.AddPanelArray({ panelArray }));
                    
                    // Fallback timeout for selection protection
                    const timeoutMs = panelArray.numberOfPanels > 1000 ? 
                        Util.SELECTION_PROTECTION_TIMEOUT_EXTENDED_MS : 
                        Util.SELECTION_PROTECTION_TIMEOUT_MS;
                    
                    timer(timeoutMs).subscribe(() => {
                        this.store.dispatch(fromUiActions.UnprotectSelection());
                    });
                    
                    if(triggerURSnapshot == undefined || triggerURSnapshot)
                      this.store.dispatch(fromUndoRedoActions.TriggerURSnapshot({action: fromPanelArrayActions.AddPanelArray, notification: panelArray.name+ ' '+ this.translateMsg('design.undoRedo.add')}))
                }
                hasPanelAdded = true
                return
            })
        return hasPanelAdded

    }

    storeSinglePanel(tempPanelObj: any, panelArrayId: string, maxPillarId: number) {
        const obj: { panel?: Panel, panelArray?: PanelArray } = {};
        const singlePanelSubscription = this.store
            .select(fromPanelArraySelectors.selectPanelArraysByIds([panelArrayId]))
            .pipe(take(1))
            .subscribe((panelArray: (PanelArray | undefined)[]) => {
                if (!(panelArray && panelArray.length && panelArray[0] != undefined)) {
                  console.error(
                    `StoreUtil.storeSinglePanel: Panel Array ID: ${panelArrayId} not found in store. Cannot add Single Panel to Store.`
                  );
                  return;
                } else {
                    // Add new frame for single panel
                    const frame: Frame = {
                        id: generateUniqueId(storeIdPrefix.frame),
                        panelIds: [tempPanelObj.name],
                        panelArrayId: panelArray[0].id,
                        isDefaultStructure: true,
                        pillarIds: [maxPillarId+1, maxPillarId+2, maxPillarId+3, maxPillarId+4]
                    }
                    this.store.dispatch(fromFrameActions.AddOne({ frame }));

                    // !NOTE: panelArray is readonly here hence prop update won't work

                    const panel = new Panel({
                        id: tempPanelObj.name,
                        visible: true,
                        instanceId: tempPanelObj.instanceId,
                        orientation: tempPanelObj.orientation ?? panelArray[0].orientation,
                        association: panelArray[0].roofId,
                        selectConfigId: true,
                        frameId: frame.id,
                        azimuth: panelArray[0].azimuth
                    });
                    this.store.dispatch(fromPanelActions.AddOne({ panel }));
                    obj.panel = panel;

                    // Updating panel array for single panel
                    const updatedPanelArray: PanelArray = JSON.parse(JSON.stringify(panelArray[0]));
                    updatedPanelArray.panelIds.push(panel.id);
                    updatedPanelArray.frameIds.push(frame.id);
                    updatedPanelArray.numberOfPanels++;
                    updatedPanelArray.noOfPanelEast++;
                    updatedPanelArray.totalAddedPanels++;
                    updatedPanelArray.totalWatts = updatedPanelArray.numberOfPanels * updatedPanelArray.wattsPerModule;

                    obj.panelArray = updatedPanelArray;
                    
                    this.store.dispatch(
                        fromPanelArrayActions.UpsertOne({
                            panelArray: updatedPanelArray
                        })
                    );
                }
            });
        return obj;
    }

    getPanels(panelIds: string[]) {
        let panels: Panel[] = [];
        this.store.select(fromPanelSelectors.selectPanelsByIds(panelIds)).pipe(take(1)).subscribe(ps => {
            panels = ps.flatMap(p => {
                if (p) return p;
                else return [];
            })
        })
        return panels;
    }

    storeObstacle(data: ObstacleData, isCopy = false) {
        const roofId = data.roofId;

        let allObstacles: Obstacle[] = [];
        this.store
            .select(fromObstacleSelectors.allObstacles)
            .pipe(take(1))
            .subscribe((v) => {
                allObstacles = v;
                return allObstacles;
            })

        let ObsType = ''
        if (data.type == ObstacleType.walkways) {

            ObsType = 'Walkway';
        }
        else {

            ObsType = 'Obstacle';
        }

        this.translate.get(ObsType).pipe(take(1)).subscribe(res => {
            ObsType = res;
        })

        let obstName = '';
        // Always use the proper counter system for both new obstacles and copies
        // This ensures unique, incrementing counters and prevents reuse of deleted obstacle numbers
        const nextCounter = Util.getNextObstacleCounter(allObstacles, ObsType);
        obstName = ObsType + " " + nextCounter;

        const newobstacle = new Obstacle({
            ...data,

            // configurationId: configId,
            name: obstName, // TODO update this to the actual name of obstacle
            roofId: roofId,
            rotation: Math.round(data.rotation),
            vertexIds: [],
            setback: data.setback ?? 0,
            setbackVisible: data.setbackVisible ?? false,
            overflown: data.overflown ?? false,
        })

        if (data.type = ObstacleType.circle) {
            newobstacle.diameter = data.diameter;
        }

        this.store.dispatch(fromObstacleActions.AddObstacle({ obstacle: newobstacle }));

        // select the created obstacle..
        this.store.dispatch(
            fromUiActions.SelectObject({ systemDesignObject: newobstacle })
        );

        return newobstacle;

    }

    updateStoreObstacle(data: ObstacleData): Promise<boolean> {
        return new Promise((resolve) => {
            this.store
                .select(fromObstacleSelectors.selectObstacleById(data.id))
                .pipe(take(1))
                .subscribe((o) => {
                    if (o) {
                        const storeObst: Obstacle = JSON.parse(JSON.stringify(o));
                        const updateObstacle: Obstacle = {
                            ...storeObst,
                            length: data.length,
                            width: data.width,
                            height: data.height,
                            rotation: data.rotation,
                            roofAssociation: data.roofAssociation,
                            setback: data.setback!,
                            defaultHeight: data.defaultHeight,
                            roofId: data.roofId,
                            overflown: data.overflown ?? storeObst.overflown ?? false,
                        }
                        
                        const updateObstacleClone = JSON.parse(JSON.stringify(updateObstacle))
                        this.store.dispatch(fromObstacleActions.UpdateObstacle({ obstacle: updateObstacleClone, triggerURSnapshot: true }))
                        // Verify the update was successful by checking the store
                        this.store
                            .select(fromObstacleSelectors.selectObstacleById(data.id))
                            .pipe(take(1))
                            .subscribe((updatedObstacle) => {
                                if (updatedObstacle) {
                                    resolve(true);
                                } else {
                                    resolve(false);
                                }
                            });
                    } else {
                        resolve(false);
                    }
                })
        });
    }

    CheckEdgeInStore(start: Vector3, end: Vector3): Edge | undefined {
        // const pos = edge.geometry.attributes.position
        // const start = new Vector3(pos[0], pos[1], pos[1])
        // const end = new Vector3(pos[2], pos[3], pos[4])

        let startId: string | undefined;
        let endId: string | undefined;
        let prevEdge: Edge | undefined;

        this.store
            .select(fromVertexSelectors.selectVertexByPositon(start))
            .pipe(take(1))
            .subscribe((v) => startId = v?.id)

        this.store
            .select(fromVertexSelectors.selectVertexByPositon(end))
            .pipe(take(1))
            .subscribe((v) => endId = v?.id)

        if (startId && endId) {
            this.store
                .select(fromEdgeSelectors.selectEdgesByStartEndVertex(startId, endId))
                .pipe(take(1))
                .subscribe((v) => prevEdge = v)
        }

        return prevEdge;
    }

    GetRoofGutterVertices(storeRoof: RoofSection) {
        let gutterEdge: Edge | undefined;
        const edgeId = storeRoof.alignPanels? storeRoof.alignPanels : storeRoof.gutterId;
        this.store
            .select(fromEdgeSelectors.selectEdgesByIds([edgeId]))
            .pipe(take(1))
            .subscribe((v) => gutterEdge = v[0])

        let startVertex: Vertex | undefined
        let endVertex: Vertex | undefined

        if (gutterEdge) {

            this.store
                .select(fromVertexSelectors.selectVerticesByIds([gutterEdge.startVertexId]))
                .pipe(take(1))
                .subscribe((v) => startVertex = v[0]);


            this.store
                .select(fromVertexSelectors.selectVerticesByIds([gutterEdge.endVertexId]))
                .pipe(take(1))
                .subscribe((v) => endVertex = v[0]);
        }

        let gutterStart: Vector3 | undefined;
        let gutterEnd: Vector3 | undefined;
        if (startVertex && endVertex) {
            const a = new Vector3(
                startVertex.x,
                startVertex.y,
                startVertex.z
            )
            const b = new Vector3(
                endVertex.x,
                endVertex.y,
                endVertex.z
            )
            const roofMesh = this.engServ.scene.getObjectByName(storeRoof.id) as Mesh;
            const roofCenter = Util.getCenter(roofMesh);
            const points = Util.getGutterLeftRightPts(a, b, roofCenter);
            gutterStart = points.gutterLeftPt;
            gutterEnd = points.gutterRightPt;

        }
        return [gutterStart, gutterEnd];
    }

    translateMsg(val: string) {
        let translatedName = '';
        this.translate.get(val).pipe(take(1)).subscribe(res => {
          translatedName = res;
        }).unsubscribe();
        return translatedName;
    }

    GetRoofRidgeVertices(storeRoof: RoofSection) {
        let ridgeEdge: Edge | undefined;
        const edgeId = storeRoof.alignPanels? storeRoof.alignPanels : storeRoof.ridgeId;
        this.store
            .select(fromEdgeSelectors.selectEdgesByIds([edgeId]))
            .pipe(take(1))
            .subscribe((v) => ridgeEdge = v[0])

        let startVertex: Vertex | undefined
        let endVertex: Vertex | undefined

        if (ridgeEdge) {

            this.store
                .select(fromVertexSelectors.selectVerticesByIds([ridgeEdge.startVertexId]))
                .pipe(take(1))
                .subscribe((v) => startVertex = v[0]);


            this.store
                .select(fromVertexSelectors.selectVerticesByIds([ridgeEdge.endVertexId]))
                .pipe(take(1))
                .subscribe((v) => endVertex = v[0]);
        }

        let ridgeStart: Vector3 | undefined;
        let ridgeEnd: Vector3 | undefined;
        if (startVertex && endVertex) {
            const a = new Vector3(
                startVertex.x,
                startVertex.y,
                startVertex.z
            )
            const b = new Vector3(
                endVertex.x,
                endVertex.y,
                endVertex.z
            )
            const roof = this.engServ.scene.getObjectByName(storeRoof.id);
            const confirmedPlane = (roof?.userData as MeshUserData).roofConfirmedPlane;
            const roofCenter = Util.getCenter(roof?.getObjectByName(meshTypeEnum.roofPlane) as Mesh);
            const points = Util.getGutterLeftRightPts(a, b, roofCenter);
            ridgeStart = points.gutterLeftPt;
            ridgeEnd = points.gutterRightPt;

        }
        return [ridgeStart, ridgeEnd];
    }
}
